<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import BotsAPI from 'dashboard/api/bots';
import PipelinesAPI from 'dashboard/api/pipelines';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const open = ref(false);
const bots = ref([]);
const pipelines = ref([]);
const search = ref('');
const launching = ref(null); // bot.id while loading
const triggerRef = ref(null);
const popoverRef = ref(null);

const stagesById = computed(() => {
  const map = {};
  pipelines.value.forEach(p => (p.stages || []).forEach(s => { map[s.id] = { ...s, pipeline_name: p.name }; }));
  return map;
});

// { stageId|null: { stage, bots: [] } }
const grouped = computed(() => {
  const q = search.value.trim().toLowerCase();
  const filtered = bots.value.filter(b => !q || b.name.toLowerCase().includes(q));
  const groups = new Map();
  // First add stages in order so output respects pipeline.position then stage.position
  pipelines.value.forEach(p => (p.stages || []).forEach(s => groups.set(s.id, { stage: s, pipeline: p, bots: [] })));
  groups.set(null, { stage: null, pipeline: null, bots: [] });

  filtered.forEach(b => {
    const ids = (b.stage_ids && b.stage_ids.length) ? b.stage_ids : [null];
    ids.forEach(sid => {
      if (!groups.has(sid)) groups.set(null, groups.get(null) || { stage: null, pipeline: null, bots: [] });
      const target = groups.get(sid) || groups.get(null);
      if (!target.bots.find(x => x.id === b.id)) target.bots.push(b);
    });
  });

  return Array.from(groups.values()).filter(g => g.bots.length > 0);
});

async function load() {
  try {
    const [bRes, pRes] = await Promise.all([BotsAPI.get(), PipelinesAPI.get()]);
    bots.value = (bRes.data?.payload || bRes.data || []).filter(b => b.active);
    pipelines.value = pRes.data?.payload || pRes.data?.data?.payload || pRes.data || [];
  } catch (e) {
    bots.value = [];
    pipelines.value = [];
  }
}

async function toggle() {
  if (open.value) { open.value = false; return; }
  await load();
  open.value = true;
  await nextTick();
  document.addEventListener('mousedown', onOutsideClick);
  document.addEventListener('keydown', onKey);
}

function onOutsideClick(ev) {
  if (popoverRef.value?.contains(ev.target)) return;
  if (triggerRef.value?.contains(ev.target)) return;
  close();
}

function onKey(ev) {
  if (ev.key === 'Escape') close();
}

function close() {
  open.value = false;
  document.removeEventListener('mousedown', onOutsideClick);
  document.removeEventListener('keydown', onKey);
}

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onOutsideClick);
  document.removeEventListener('keydown', onKey);
});

onMounted(() => { /* lazy: only load on toggle */ });

async function launchBot(bot) {
  if (launching.value) return;
  launching.value = bot.id;
  try {
    await BotsAPI.launch(bot.id, props.conversationId);
    close();
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error lanzando el bot: ' + (e.response?.data?.error || e.message));
  } finally {
    launching.value = null;
  }
}
</script>

<template>
  <div class="relative inline-block">
    <button
      ref="triggerRef"
      type="button"
      v-tooltip.top-end="'Lanzar Salesbot'"
      class="bot-launcher-trigger w-7 h-7 rounded flex items-center justify-center transition"
      :class="open ? 'bg-amber-100 ring-1 ring-amber-400 text-amber-700' : 'text-amber-600 bg-gradient-to-br from-amber-50 to-orange-100 ring-1 ring-amber-300 hover:from-amber-100 hover:to-orange-200'"
      @click.stop="toggle"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
      </svg>
    </button>

    <div
      v-if="open"
      ref="popoverRef"
      class="absolute z-50 bottom-full mb-2 right-0 w-[360px] bg-n-background border border-n-weak rounded-lg shadow-2xl p-2"
      style="box-shadow: 0 12px 32px rgba(0,0,0,.14)"
    >
      <div class="px-2 pt-1 pb-2">
        <div class="flex items-center gap-1.5 text-[11px] text-n-slate-11 mb-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>
          <span class="font-semibold uppercase tracking-wide">Lanzar Salesbot</span>
        </div>
        <input
          v-model="search"
          type="text"
          placeholder="Buscar bot…"
          class="w-full text-xs px-2 py-1.5 rounded bg-n-alpha-black1 border border-n-weak text-n-slate-12 placeholder-n-slate-11 focus:outline-none focus:border-blue-400"
        />
      </div>

      <div class="px-1 pb-1 max-h-[280px] overflow-y-auto bot-launcher-scroll">
        <div v-if="grouped.length === 0" class="px-3 py-6 text-center text-[11px] text-n-slate-11">
          {{ search ? 'Sin resultados' : 'No hay bots activos' }}
        </div>

        <template v-for="g in grouped" :key="g.stage?.id || 'sin-etapa'">
          <div class="px-2 pt-2 pb-1 flex items-center gap-1.5">
            <span
              class="w-2 h-2 rounded-full"
              :style="{ background: g.stage?.color || '#94a3b8' }"
            />
            <span class="text-[10px] font-semibold text-n-slate-11 uppercase tracking-wide truncate">
              {{ g.stage ? g.stage.name : 'Sin etapa específica' }}
            </span>
            <span v-if="g.pipeline" class="text-[9px] text-n-slate-11 ml-auto">
              {{ g.pipeline.name }}
            </span>
          </div>

          <button
            v-for="b in g.bots"
            :key="`${g.stage?.id || 'na'}-${b.id}`"
            type="button"
            class="bot-launcher-row w-full flex items-center gap-2 px-2 py-1.5 rounded text-left disabled:opacity-50"
            :disabled="launching === b.id"
            @click="launchBot(b)"
          >
            <span class="text-base">🤖</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs font-medium text-n-slate-12 truncate">{{ b.name }}</div>
              <div class="text-[10px] text-n-slate-11 truncate">
                {{ b.trigger_summary || 'Manual' }}
                <span v-if="b.conversion_rate != null && b.launches_total > 0">
                  · {{ b.conversion_rate }}% conv · {{ b.launches_total }} runs
                </span>
              </div>
            </div>
            <span v-if="launching === b.id" class="text-[10px] text-n-slate-11">…</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bot-launcher-row { transition: background 120ms ease; }
.bot-launcher-row:hover:not([disabled]) { background: var(--color-n-alpha-black1, rgba(0,0,0,.04)); }
.bot-launcher-scroll::-webkit-scrollbar { width: 6px; }
.bot-launcher-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
</style>
